"use client";
import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Col,
  Divider,
  List,
  Row,
  Space,
  Typography,
} from "antd";

import TimeButton from "../../../../components/TimeButton";
import { DB_DATE_FORMAT } from "@/consts";
import { useBookingApi } from "@/app/components/NewAppointmentModal/bookingApi";
import { UserData, UserOpeningsData, ServiceOption } from "@/lib/api/types";
import OpeningLoading from "@/app/components/OpeningLoading";
import { MinBookingNoticeNote } from "@/app/components/BookingLimitNotes";
import { AppointmentForm } from "@/lib/hooks/useAppointment";
import { CalendarOutlined } from "@ant-design/icons";
import { LocationServiceSelectionError } from "@/lib/api/openingsLocations";

interface IProps {
  userId: string;
  userPathName: string;
  userName: string;
  date: string;
  services: (UserData["services"][number] & { option?: ServiceOption })[];
  onTimeClick: (args: AppointmentForm) => void;
  onRefresh?: () => void;
}

const DisplayNextOpenings = ({
  userId,
  userPathName,
  userName,
  date,
  services,
  onTimeClick,
  onRefresh,
}: IProps) => {
  const bookingApi = useBookingApi();
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState<(UserOpeningsData & { date: string })[]>([]);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasSelectionMismatch, setHasSelectionMismatch] = useState(false);
  const queryKey = JSON.stringify([
    userPathName,
    date,
    services.map((service) => [
      service._id,
      service.referenceId,
      service.duration,
      service.option?.id,
      service.option?.referenceId,
      service.option?.duration,
    ]),
  ]);
  const activeRequest = useRef(0);
  const [stateQueryKey, setStateQueryKey] = useState(queryKey);

  const onSearch = async () => {
    const requestId = ++activeRequest.current;
    const requestedQueryKey = queryKey;
    let _date = date;
    if (dayjs(date).isSame(dayjs(), "day")) {
      _date = dayjs(date).add(1, "days").format(DB_DATE_FORMAT);
    }

    setStateQueryKey(requestedQueryKey);
    setLoading(true);
    setData([]);
    setHasLoadError(false);
    setHasSelectionMismatch(false);
    try {
      const res = await bookingApi.getUserNextOpeningsByDuration(
        userPathName,
        _date,
        services,
      );
      if (requestId !== activeRequest.current) {
        return;
      }
      setData(
        [...res].sort(
          (a, b) =>
            a.date.localeCompare(b.date) ||
            (a.openings[0] ?? "").localeCompare(b.openings[0] ?? "") ||
            a.schedule.locationId.localeCompare(b.schedule.locationId) ||
            a.schedule._id.localeCompare(b.schedule._id),
        ),
      );
    } catch (error) {
      if (requestId !== activeRequest.current) {
        return;
      }
      // An outage must not read as "no openings" — that sends customers away
      // from a stylist who may be wide open.
      if (error instanceof LocationServiceSelectionError) {
        setHasSelectionMismatch(true);
      } else {
        console.error(error);
        setHasLoadError(true);
      }
    } finally {
      if (requestId === activeRequest.current) {
        setLoading(false);
      }
    }
  };

  // State belongs to the query that produced it. Prop changes hide an old
  // result immediately, before an effect could run, and the request guard
  // above prevents that old promise from overwriting a newer search.
  const isCurrentQuery = stateQueryKey === queryKey;
  const currentData = isCurrentQuery ? data : [];
  const currentHasLoadError = isCurrentQuery && hasLoadError;
  const currentHasSelectionMismatch =
    isCurrentQuery && hasSelectionMismatch;
  const currentIsLoading = isCurrentQuery && isLoading;

  if (currentIsLoading) {
    return <OpeningLoading />;
  }

  return (
    <>
      {currentData.length > 0 ? (
        <>
          {currentData.map((item) => {
            return (
              <List
                key={`${item.date}:${item.schedule.locationId}:${item.schedule._id}:${item.schedule.memberId ?? ""}`}
              >
                <List.Item>
                  <List.Item.Meta
                    description={
                      <>
                        <Space className="pb-1">
                          <Typography.Text strong>{item.date}</Typography.Text>
                          <Divider type="vertical" />
                          <Typography.Text type="secondary">
                            {item.schedule.locationName}
                          </Typography.Text>
                        </Space>
                        {/* Per location, from that business's own resolved
                            services (see DisplaySchedules). */}
                        <div className="pb-1">
                          <MinBookingNoticeNote services={item.services ?? services} />
                        </div>

                        <Row gutter={[8, 8]}>
                          {item.openings.map((time: string, i: number) => {
                            return (
                              <Col
                                key={`${item.date}:${item.schedule.locationId}:${item.schedule._id}:${time}:${i}`}
                                span={6}
                              >
                                <TimeButton
                                  onClick={() => {
                                    onTimeClick({
                                      // A stylist working at two businesses
                                      // has a different memberId in each;
                                      // book with the one this slot was
                                      // computed for. Mirrors DisplaySchedules.
                                      userId: item.schedule.memberId ?? userId,
                                      stylistName: userName,
                                      scheduleId: item.schedule._id,
                                      // The registry schedule id for the slot's
                                      // location. A second resolvable
                                      // identifier is what lets the server-side
                                      // consistency guard DETECT a
                                      // member/location disagreement instead of
                                      // trusting a single signal.
                                      locationId: item.schedule.locationId,
                                      locationName: item.schedule.locationName,
                                      services: item.services ?? services,
                                      time,
                                      date: item.date,
                                    });
                                  }}
                                  time={time}
                                  isSelected={false}
                                />
                              </Col>
                            );
                          })}
                        </Row>
                      </>
                    }
                  ></List.Item.Meta>
                </List.Item>
              </List>
            );
          })}
        </>
      ) : currentHasSelectionMismatch ? (
        <Alert
          type="warning"
          showIcon
          message="The selected service is no longer available."
          description="Please choose a different service or option."
        />
      ) : currentHasLoadError ? (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="We couldn't load availability."
          description="Please check your connection and try again."
          action={
            <Button size="small" onClick={onSearch}>
              Try again
            </Button>
          }
        />
      ) : (
        <div
          style={{
            border: "1px solid #000",
            padding: "16px",
            borderRadius: "8px",
          }}
        >
          <Typography.Paragraph>
            No openings found for this date. Check availability on other days.
          </Typography.Paragraph>
          <Button
            color="default"
            variant="solid"
            icon={<CalendarOutlined />}
            type="primary"
            onClick={onSearch}
          >
            Next Availability
          </Button>
        </div>
      )}
    </>
  );
};

export default DisplayNextOpenings;
